import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FormField } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { toast } from "@/hooks/useToast"
import { getPreventionProgramsConnectionId } from "@/modules/patients/detail/health-information/condition/lib/get-prevention-programs-connection"
import { useSavePreventionProgramEngagementsMutation } from "@/modules/patients/services/useSavePreventionProgramEngagementsMutation"
import { Country, PreventionProgramDismissReason } from "@/types/__generated__/union-types"

export const dismissReasonOptions: Array<{ value: PreventionProgramDismissReason; label: string }> = [
  {
    value: "PATIENT_IS_NOT_INTERESTED",
    label: "patient.healthInformation.eligiblePreventionPrograms.rejectReasonContent.not_interestd",
  },
  {
    value: "PATIENT_SELF_ORGANIZES",
    label: "patient.healthInformation.eligiblePreventionPrograms.rejectReasonContent.will_take_care",
  },
  {
    value: "PATIENT_IS_ALREADY_IN_PROGRAM",
    label: "patient.healthInformation.eligiblePreventionPrograms.rejectReasonContent.already_in_program",
  },
  {
    value: "PATIENT_HAS_NO_INCREASED_RISK",
    label: "patient.healthInformation.eligiblePreventionPrograms.rejectReasonContent.no_increased_risk",
  },
  {
    value: "APPOINTMENT_ALREADY_BOOKED_THROUGH_MEDGATE",
    label: "patient.healthInformation.eligiblePreventionPrograms.rejectReasonContent.already_book",
  },
]

interface PreventionProgramDialogData {
  key: string
  localizedInstruction: { de: string; en: string; fr: string; it: string }
}

interface PreventionProgramsDialogProps {
  preventionProgramData: PreventionProgramDialogData
  patientId: string
  country: Country
  onClose: VoidFunction
  onDismiss?: VoidFunction
  isEditPreventionProgramsMode: boolean
  currentDismissReason?: PreventionProgramDismissReason
}

const DEFAULT_REACTIVATION_PERIOD = 1

const PreventionProgramsSchema = z.object({
  rejectReason: z.custom<PreventionProgramDismissReason>(),
})
type PreventionProgramsForm = z.infer<typeof PreventionProgramsSchema>

export const PreventionProgramsDialog = ({
  preventionProgramData,
  patientId,
  country,
  onClose,
  onDismiss,
  isEditPreventionProgramsMode,
  currentDismissReason,
}: PreventionProgramsDialogProps) => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(true)
  const [reactivationPeriod, setReactivationPeriod] = useState(DEFAULT_REACTIVATION_PERIOD)
  const [savePreventionProgramEngagements, isUpdating] = useSavePreventionProgramEngagementsMutation(country)

  const form = useForm<PreventionProgramsForm>({
    resolver: zodResolver(PreventionProgramsSchema),
    defaultValues: { rejectReason: currentDismissReason ?? "PATIENT_IS_NOT_INTERESTED" },
  })

  const { handleSubmit, control } = form

  useEffect(() => {
    if (currentDismissReason) {
      handleSelectRequesting(currentDismissReason)
    }
  }, [currentDismissReason])

  const handleSavePreventionProgram = (data: PreventionProgramsForm) => {
    savePreventionProgramEngagements(
      {
        patientId,
        engagements: [
          {
            preventionProgramKey: preventionProgramData.key,
            status: "DISMISSED",
            dismissReason: data.rejectReason,
          },
        ],
      },
      [getPreventionProgramsConnectionId(patientId)],
      (response) => {
        const error = response.savePreventionProgramEngagements?.errors?.[0]
        const errMessage = error && "message" in error ? error.message : undefined
        handleSavePreventionProgramEngagementCompleted(errMessage)
      },
    )
  }

  const handleSavePreventionProgramEngagementCompleted = (errorMessage?: string) => {
    if (errorMessage) {
      toast({
        description: errorMessage,
      })
    } else {
      onClose()
      onDismiss?.()
    }
  }

  const handleSelectRequesting = (value: PreventionProgramDismissReason) => {
    setReactivationPeriod(value === "PATIENT_HAS_NO_INCREASED_RISK" ? 5 : DEFAULT_REACTIVATION_PERIOD)
    setIsVisible(!(value === "PATIENT_IS_ALREADY_IN_PROGRAM" || value === "APPOINTMENT_ALREADY_BOOKED_THROUGH_MEDGATE"))
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContentCustom
        className="min-w-150.5"
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="prevention-dialog">
        <DialogHeader>
          <DialogTitle>
            {t(
              isEditPreventionProgramsMode
                ? "patient.healthInformation.eligiblePreventionPrograms.dialog.editTitle"
                : "patient.healthInformation.eligiblePreventionPrograms.dialog.title",
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-start gap-2 p-6">
          <FormField
            name={"rejectReason"}
            control={control}
            render={({ field: { value: val, onChange } }) => (
              <RadioGroup
                className="grid w-full grid-cols-1 gap-2"
                value={val}
                onValueChange={(newValue: PreventionProgramDismissReason) => {
                  onChange(newValue)
                  handleSelectRequesting(newValue)
                }}>
                {dismissReasonOptions.map((item) => {
                  const { label, value } = item
                  return (
                    <RadioGroupItem key={value} value={value} className="min-w-full" data-testid={value.toLowerCase()}>
                      <RadioGroupLabel>{t(label)}</RadioGroupLabel>
                    </RadioGroupItem>
                  )
                })}
              </RadioGroup>
            )}
          />
          {isVisible && (
            <Banner type="inline" variant="info" hasIcon={true} data-testid="prevention-info">
              <span>
                {t("patient.healthInformation.eligiblePreventionPrograms.dialog.rejectReason", {
                  year: reactivationPeriod,
                })}
              </span>
            </Banner>
          )}
        </div>
        <DialogFooter className="flex p-6 pt-4">
          <Button
            className="rounded-[20px] px-6 py-2.5"
            variant="secondary"
            onClick={onClose}
            data-testid="prevention-cancel-button">
            {t("common.button.cancel")}
          </Button>
          <Button
            isLoading={isUpdating}
            variant="primary"
            className="ml-0 rounded-[20px] px-6 py-2.5"
            onClick={handleSubmit(handleSavePreventionProgram)}
            data-testid="prevention-save-button">
            {t("common.button.save")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}
